import React from "react";
import {
  SkeletonAvatar,
  SkeletonBlock,
  SkeletonChart,
  SkeletonText,
} from "./Skeleton";
import ListSkeleton from "./ListSkeleton";

/**
 * Tailored Profile skeleton — ONE layout, two views.
 *
 * `variant="private"` (/profile) and `variant="public"` (/profile/:userId) share
 * every component here; the public view simply drops the owner-only elements
 * (Owner Tools, QUSHi Coins) that its layout doesn't have. There is deliberately
 * no second profile skeleton.
 *
 * What is instant and what is grey, per spec:
 *   • Header + nav chrome ...... instant (rendered by the screen, not here)
 *   • Avatar ................... skeleton circle
 *   • Username / handle ........ skeleton text lines
 *   • Stats row ................ stat blocks
 *   • QUSHi Coins card ......... card shell instant, value skeleton
 *   • The Record card .......... card shell instant, metrics skeleton
 *   • Chart area ............... chart rectangle at its FINAL height
 *   • Performance metric rows .. stat blocks
 *   • Owner tools .............. chrome instant, NO skeleton on the buttons
 *
 * No message, no centred icon, no spinner, no progress bar. And no placeholder
 * values — never "$0.00", "0.00%" or "—" while loading; each of those reads as
 * a real balance, a flat position, or an empty record.
 *
 * Interactive chrome (tabs, Edit profile, Copy referral) renders in its real
 * unavailable state rather than as grey blocks, because users tap skeletons.
 */

/* Label above a value, value greyed. Matches the ProfileRecordCard `Stat`
   cell — its border and padding are real so the grid can't shift. */
const StatCell = () => (
  <div className="flex flex-col border border-[#F3F4F6] p-3">
    <SkeletonBlock height={10} width={70} />
    <SkeletonBlock height={18} width={84} className="mt-2" />
  </div>
);

export const RecordCardSkeleton = ({
  chartRight = true,
  // Card chrome is real text, so it has to be the RIGHT text from frame one.
  // Left at the sitewide defaults the league profile flashed "The Record /
  // Verified performance on QUSHi" and then corrected itself to "League Record"
  // the moment the profile landed. Same source as ProfileRecordCard.
  title,
  subtitle,
}) => (
  <div className="mb-4 rounded-xl border border-[#F3F4F6] bg-white overflow-hidden">
    <div className="p-5">
      {/* Card chrome is REAL — the title and strapline don't depend on data, so
          they must not be grey blocks. */}
      <div className="flex items-center gap-2">
        <h2 className="text-[20px] font-bold font-poppins text-[#1A1A1A]">
          {title || "The Record"}
        </h2>
        <img src="/check-badge-icon.svg" alt="verified" className="w-5 h-5" />
      </div>
      <p className="text-[13px] text-secondary mt-1 font-inter">
        {subtitle || "Verified performance on QUSHi"}
      </p>

      {chartRight ? (
        <div className="flex items-start gap-4 mt-5">
          <div className="shrink-0">
            <p className="text-[12px] tracking-wide text-secondary font-inter uppercase">
              Current Equity
            </p>
            {/* 28px bold figure → a 28px-tall block, so nothing shifts. */}
            <SkeletonBlock height={28} width={132} className="mt-1" />
          </div>
          <div className="flex-1 min-w-0">
            <SkeletonChart height={56} className="my-2" />
          </div>
        </div>
      ) : (
        <>
          <p className="text-[13px] text-secondary mt-4 mb-1.5 font-inter uppercase">
            Current Equity
          </p>
          <SkeletonBlock height={30} width={150} />
          <SkeletonChart height={96} className="my-2" />
        </>
      )}
    </div>

    {/* Six metric cells: return, P&L, drawdown, win rate, profit factor, hold
        time. Grey blocks — a directional colour here would imply a result. */}
    <div className="grid grid-cols-3 mt-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <StatCell key={i} />
      ))}
    </div>

    <div className="flex justify-between items-center mt-5 p-4 border-t border-[#F3F4F6] bg-[#F9FAFB]">
      <span className="flex items-center gap-1.5 text-[13px] text-secondary">
        <img src="/check-badge-icon.svg" alt="" className="w-4 h-4" />
        Verified by QUSHi
      </span>
      <span className="text-[13px] text-secondary">Updated often</span>
    </div>
  </div>
);

/* Owner-only rail. Chrome is instant; only the coin balance is grey. The
   buttons are real chrome in their unavailable state — never skeletons, because
   a grey block that looks like a button gets tapped. */
export const OwnerToolsSkeleton = () => (
  <div className="mb-4 flex flex-col gap-4">
    <div className="w-full border rounded-xl border-[#F3F4F6] p-3 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <img
          src="/qushi-coins-icon-com.png"
          alt="QUSHi Coins"
          className="h-10 w-10 object-contain shrink-0"
        />
        <div className="min-w-0 flex-1">
          <p className="font-bold text-base font-poppins text-[#6B7280] flex items-center justify-between gap-2 mb-1">
            QUSHi Coins
            {/* Balance only. Never a 0 here — it would read as "you have none". */}
            <SkeletonBlock height={20} width={56} className="ml-auto" />
          </p>
          <p className="text-[10px] text-secondary leading-snug">
            Earn them by trading, learning, and competing.
          </p>
        </div>
      </div>
    </div>

    <div className="w-full border rounded-xl border-[#F3F4F6] p-4">
      <p className="flex items-center gap-2 text-[15px] font-bold font-poppins text-[#1A1A1A] mb-3">
        Owner Tools
        <img src="/lock-icon.svg" alt="lock-icon" />
      </p>
      <div className="border-y border-[#F9FAFB] py-4 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-sm font-poppins text-[#1A1A1A] mb-1">
            The Comeback
          </p>
          <p className="text-[11px] text-secondary leading-snug">
            Fresh $1M. New record. New start date.
          </p>
        </div>
        <div className="flex flex-col items-end shrink-0">
          <span className="text-[10px] font-semibold text-[#9CA3AF] mb-1">
            $4.99
          </span>
          {/* Real button, existing unavailable state, does not accept input
              until the data it acts on is here. */}
          <button
            type="button"
            disabled
            aria-disabled="true"
            className="px-4 py-2 text-[11px] rounded-lg border border-primaryColor text-primaryColor bg-[#004C50]/5 font-semibold whitespace-nowrap opacity-50"
          >
            Learn more
          </button>
        </div>
      </div>
      <div className="pt-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-4 min-w-0">
          <img
            src="/doublecircleicon.png"
            alt="referral"
            className="w-9 h-9 object-contain shrink-0"
          />
          <div className="min-w-0 flex-1">
            <p className="font-bold text-sm font-poppins text-[#1A1A1A]">
              Referral link
            </p>
            <SkeletonBlock height={10} width="70%" className="mt-1.5" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

export const TopHoldingsSkeleton = () => (
  <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-4 feed-shadow">
    <p className="text-[15px] font-bold font-poppins text-[#1A1A1A] mb-3">
      Top Holdings
    </p>
    <ListSkeleton rows={3} avatar={false} columns={1} rowClassName="py-3" />
  </div>
);

/* Tabs are interactive chrome: real labels, real bar, input blocked until the
   profile is here. No grey pills — users would tap them. */
const TABS = ["Posts", "Holdings", "Trade History", "Performance"];

const TabsChrome = ({ className = "" }) => (
  <div className={className}>
    {TABS.map((label, i) => (
      <button
        key={label}
        type="button"
        disabled
        aria-disabled="true"
        className={`pb-3 whitespace-nowrap text-[13px] lg:text-[15px] border-b-2 -mb-px ${
          i === 0
            ? "text-primaryColor font-semibold border-primaryColor"
            : "text-[#5F6F72] font-medium border-transparent"
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

/* Identity block — avatar circle plus username / handle text lines. */
const HeaderIdentitySkeleton = ({ avatarSize, nameHeight, nameWidth }) => (
  <div className="flex items-start gap-4 lg:gap-6">
    <SkeletonAvatar size={avatarSize} />
    <div className="flex-1 min-w-0 pt-1">
      <SkeletonBlock height={nameHeight} width={nameWidth} />
      <SkeletonBlock height={12} width={150} className="mt-3" />
      <SkeletonText lines={2} height={12} lastLineWidth="45%" className="mt-4" />
    </div>
  </div>
);

/**
 * Header skeleton. Mirrors PublicProfileHeader's `variant` / `only` props so it
 * can stand in for it one-for-one while the profile request is still out.
 */
export const ProfileHeaderSkeleton = ({
  variant = "public",
  only,
  // Same reason as RecordCardSkeleton above: the rank LABEL is real chrome, so
  // a league profile must not paint "LEADERBOARD" and then correct itself to
  // "LEAGUE RANK" / "GAME RANK".
  rankLabel = "LEADERBOARD",
}) => {
  const isPrivate = variant === "private";
  return (
    <>
      {only !== "desktop" && (
        <div className="lg:hidden flex flex-col gap-4 px-4 pt-0">
          <HeaderIdentitySkeleton
            avatarSize={80}
            nameHeight={22}
            nameWidth="62%"
          />
          <div className="border-t border-[#F3F4F6] py-5 my-2 flex flex-col gap-4">
            <div>
              <SkeletonBlock height={30} width={110} />
              <p className="mt-1.5 text-[12px] tracking-[0.18em] text-[#A0A3A8] font-semibold">
                {rankLabel}
              </p>
            </div>
            <SkeletonBlock height={14} width="80%" />
          </div>
        </div>
      )}

      {only !== "mobile" && (
        <div
          className={`hidden lg:block mb-4 lg:mb-0 ${
            isPrivate ? "lg:pb-8" : "lg:p-8 lg:pb-0"
          }`}
        >
          <div className="rounded-2xl border border-[#F3F4F6] p-6">
            <div className="flex items-start gap-6">
              <HeaderIdentitySkeleton
                avatarSize={96}
                nameHeight={26}
                nameWidth="45%"
              />
              {!isPrivate && (
                <div className="shrink-0 flex flex-col items-start gap-3 min-w-[335px]">
                  <div>
                    <SkeletonBlock height={34} width={120} />
                    <p className="mt-1.5 text-[12px] tracking-[0.18em] text-[#A0A3A8] font-semibold">
                      {rankLabel}
                    </p>
                  </div>
                  <SkeletonBlock height={14} width={260} />
                  <div className="flex gap-3 mt-5">
                    <button
                      type="button"
                      disabled
                      aria-disabled="true"
                      className="px-4 py-1.5 rounded-lg bg-white text-primaryColor font-semibold font-poppins border border-primaryColor opacity-50 w-max"
                    >
                      Follow
                    </button>
                    <button
                      type="button"
                      disabled
                      aria-disabled="true"
                      className="px-4 py-1.5 rounded-lg bg-white text-[#374151] font-semibold font-poppins border border-[#D1D5DB] opacity-50 w-max"
                    >
                      Message
                    </button>
                  </div>
                </div>
              )}
            </div>
            {isPrivate && (
              <div className="mt-5 pt-5 border-t border-gray-100">
                <SkeletonBlock height={34} width={120} />
                <p className="mt-1.5 text-[12px] tracking-[0.18em] text-[#A0A3A8] font-semibold">
                  {rankLabel}
                </p>
                <SkeletonBlock height={14} width={300} className="mt-3" />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const ProfileSkeleton = ({ variant = "private" }) => {
  const isPrivate = variant === "private";

  return (
    <div
      className="md:block rounded-2xl bg-white w-full max-w-[60rem] lg:max-w-none mx-auto"
      aria-busy="true"
      aria-label="Profile loading"
    >
      <main className="h-full bg-white justify-center">
        {/* ===================== MOBILE ===================== */}
        <div className="lg:hidden flex flex-col gap-4 px-4 pt-0">
          <HeaderIdentitySkeleton
            avatarSize={80}
            nameHeight={22}
            nameWidth="62%"
          />
          {/* Leaderboard rank + "trading since / trades / fans" meta strip.
              The LEADERBOARD label is real chrome; the rank is grey — an
              "Unranked" placeholder would state a fact we don't have yet. */}
          <div className="border-t border-[#F3F4F6] py-5 my-2 flex flex-col gap-4">
            <div>
              <SkeletonBlock height={30} width={110} />
              <p className="mt-1.5 text-[12px] tracking-[0.18em] text-[#A0A3A8] font-semibold">
                LEADERBOARD
              </p>
            </div>
            <SkeletonBlock height={14} width="80%" />
          </div>
        </div>

        <div className="lg:flex lg:items-start lg:gap-8 lg:p-8">
          <div className="lg:flex-1 lg:min-w-0">
            {/* ===================== DESKTOP HEADER ===================== */}
            <div
              className={`hidden lg:block mb-4 lg:mb-0 ${
                isPrivate ? "lg:pb-8" : "lg:p-8 lg:pb-0"
              }`}
            >
              <div className="rounded-2xl border border-[#F3F4F6] p-6">
                <div className="flex items-start gap-6">
                  <HeaderIdentitySkeleton
                    avatarSize={96}
                    nameHeight={26}
                    nameWidth="45%"
                  />
                  {!isPrivate && (
                    <div className="shrink-0 flex flex-col items-start gap-3 min-w-[335px]">
                      <div>
                        <SkeletonBlock height={34} width={120} />
                        <p className="mt-1.5 text-[12px] tracking-[0.18em] text-[#A0A3A8] font-semibold">
                          LEADERBOARD
                        </p>
                      </div>
                      <SkeletonBlock height={14} width={260} />
                      {/* Follow / Message: real buttons, unavailable until the
                          profile arrives (we don't yet know the follow state). */}
                      <div className="flex gap-3 mt-5">
                        <button
                          type="button"
                          disabled
                          aria-disabled="true"
                          className="px-4 py-1.5 rounded-lg bg-white text-primaryColor font-semibold font-poppins border border-primaryColor opacity-50 w-max"
                        >
                          Follow
                        </button>
                        <button
                          type="button"
                          disabled
                          aria-disabled="true"
                          className="px-4 py-1.5 rounded-lg bg-white text-[#374151] font-semibold font-poppins border border-[#D1D5DB] opacity-50 w-max"
                        >
                          Message
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {isPrivate && (
                  <div className="mt-5 pt-5 border-t border-gray-100">
                    <SkeletonBlock height={34} width={120} />
                    <p className="mt-1.5 text-[12px] tracking-[0.18em] text-[#A0A3A8] font-semibold">
                      LEADERBOARD
                    </p>
                    <SkeletonBlock height={14} width={300} className="mt-3" />
                  </div>
                )}
              </div>
            </div>

            {/* ===================== LEFT CONTENT CARD ===================== */}
            <div className="mb-4 lg:border lg:border-[#F3F4F6] lg:rounded-2xl lg:bg-white">
              {/* Mobile: The Record + owner tools sit in the flow here; on
                  desktop they live in the right rail. */}
              <div className="lg:hidden">
                <RecordCardSkeleton chartRight={isPrivate} />
                {isPrivate && <OwnerToolsSkeleton />}
              </div>

              <TabsChrome className="lg:hidden mt-4 flex justify-between gap-2 border-b border-[#F3F4F6] overflow-hidden" />
              <TabsChrome className="hidden lg:flex lg:mx-8 mt-4 gap-6 border-b border-[#F3F4F6] flex-wrap" />

              {/* Posts tab is the default landing tab. Rows, not a spinner. */}
              <div className="px-4 lg:px-8 pb-2">
                <ListSkeleton rows={3} avatarSize={44} columns={0} rowClassName="py-5" />
              </div>
            </div>
          </div>

          {/* ===================== DESKTOP RIGHT RAIL ===================== */}
          <div className="hidden lg:block lg:w-[360px] lg:shrink-0">
            <RecordCardSkeleton chartRight />
            {isPrivate && <OwnerToolsSkeleton />}
            <TopHoldingsSkeleton />
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfileSkeleton;
